import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { kebabCase } from 'lodash'
import { useQuery, useReactiveVar } from '@apollo/client'

import { BottomSheet } from '@src/components/BottomSheet/BottomSheet'
import { LoadingSpinner } from '@src/components/Loaders/LoadingSpinner'
import { screenResolutionVar } from '@src/models/screenResolution'
import { GetOutletsByRestaurantIdDocument } from './queries/__generated__/GetOutletsByRestaurantId.graphql-interface'

import {
  ContentContainer,
  HeaderContainer,
  Title,
  Description,
  LinksContainer,
  LinkItem,
} from './OutletPreviewSelectModal.styles'
import { useBottomSheetStack } from '../../../hooks/useBottomSheetStack/useBottomSheetStack'

export const OutletPreviewSelectModal: FC<{
  showModal: boolean
  outletId: string
}> = ({ showModal = false, outletId }) => {
  const { t } = useTranslation('outletOverview')
  const { outletPreviewPickerBottomSheetController } = useBottomSheetStack()
  const { height } = useReactiveVar(screenResolutionVar)

  const { data, loading } = useQuery(GetOutletsByRestaurantIdDocument, {
    variables: { outletId },
    onCompleted: data => {
      if (showModal && data.restaurantByOutletId.outlets.length > 1)
        outletPreviewPickerBottomSheetController.open()
    },
  })

  const currentOutlet = data?.restaurantByOutletId?.outlets.find(
    outlet => outlet.id === outletId
  )

  return (
    <BottomSheet
      bottomSheetController={outletPreviewPickerBottomSheetController}
      maxWidth={584}
      height={0.55}
    >
      {!loading &&
      data &&
      currentOutlet &&
      outletPreviewPickerBottomSheetController.isOpen ? (
        <ContentContainer $height={height * 0.5}>
          <HeaderContainer>
            <Title>{data.restaurantByOutletId.name}</Title>
            <Description>{t('outlet_preview_description')}</Description>
            {currentOutlet && (
              <>
                {t('outlet_preview_current')}
                <strong>{currentOutlet.name}</strong>
              </>
            )}
          </HeaderContainer>
          <LinksContainer>
            {data.restaurantByOutletId.outlets.map(outlet => (
              <LinkItem
                key={outlet.id}
                onClick={() => outletPreviewPickerBottomSheetController.close()}
                to={`/takeaways/${outlet.id}/${kebabCase(
                  outlet.name
                )}/menu?showInactive=1&outletPreview=1`}
              >
                {currentOutlet.id === outlet.id ? (
                  <strong>{outlet.name}</strong>
                ) : (
                  <>{outlet.name}</>
                )}
              </LinkItem>
            ))}
          </LinksContainer>
        </ContentContainer>
      ) : (
        <LoadingSpinner />
      )}
    </BottomSheet>
  )
}
